import { useEffect, useRef, useState } from 'react';

export default function ProblemSection() {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-16 items-center">
          {/* Left — image */}
          <div className={`lg:col-span-5 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            <div className="relative">
              <div className="w-full h-[480px] rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Empty%20hotel%20lobby%20with%20no%20guests%20during%20peak%20hours%20showing%20business%20struggle%2C%20vacant%20reception%20desk%20with%20ringing%20phone%20unanswered%2C%20dark%20shadows%20in%20corners%2C%20single%20wilted%20plant%20on%20counter%2C%20dust%20visible%20on%20surfaces%2C%20warm%20amber%20sunset%20light%20streaming%20through%20windows%20highlighting%20emptiness%2C%20professional%20architectural%20photography%2C%20melancholic%20atmosphere%20of%20failing%20hospitality%20business&width=600&height=800&seq=problem-empty-lobby-v2&orientation=portrait"
                  alt="Empty hotel lobby showing business struggle and poor management"
                  className="w-full h-full object-cover object-top"
                />
              </div>

            </div>
          </div>

          {/* Right — copy */}
          <div className={`lg:col-span-7 transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            <div className="inline-flex items-center space-x-2 mb-6">
              <div className="h-px w-8 bg-amber-500"></div>
              <span className="text-xs font-medium text-amber-600 uppercase tracking-[0.25em]">The Problem</span>
            </div>

            <h2 className="text-4xl lg:text-5xl font-light text-gray-900 leading-tight mb-8">
              Why Most Hotel Owners
              <br />
              <span className="italic text-gray-500">End Up Switching Partners</span>
            </h2>

            <div className="space-y-6">
              <p className="text-lg text-gray-600 font-light leading-relaxed">
                Most management companies charge the same base fee whether your RevPAR grows or flatlines. They assign a junior rep, promise a partnership, and move on to the next property. You&apos;re one of 120 — not a priority.
              </p>
              <p className="text-lg text-gray-700 font-light leading-relaxed">
                360 exists for one reason: to give independent Florida hotel owners a partner with actual skin in the game.
              </p>
            </div>

            {/* Pain points */}
            <div className="mt-10 grid sm:grid-cols-2 gap-4">
              {[
                { icon: 'ri-close-circle-line', text: 'Junior reps, no senior accountability', color: 'text-red-400' },
                { icon: 'ri-close-circle-line', text: 'Generic national strategies, not local', color: 'text-red-400' },
                { icon: 'ri-close-circle-line', text: 'You\'re a number, not a partner', color: 'text-red-400' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 bg-red-50 rounded-xl px-4 py-3">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className={`${item.icon} ${item.color} text-lg`}></i>
                  </div>
                  <span className="text-sm text-gray-700 font-light">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
